/**
 * Device marks for fraud prevention (ADR-0044: one person, one profile). Sent on sign-up, sign-in and checkout only.
 *
 *   X-Device-Fp   SHA-256 of stable browser traits: platform, screen, time zone, languages, CPU threads, device memory,
 *                 touch points, a canvas drawing and the WebGL renderer. Only the hash leaves the browser.
 *   X-Device-Id   a random id this browser keeps (localStorage, mirrored in a first-party cookie) — nothing personal.
 *
 * Everything is best effort: a blocked canvas, storage or WebCrypto (an insecure origin) drops that part, never the
 * request.
 */
const KEY = "prop.device";
const COOKIE = "pdid";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function newId(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function cookieId(): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]+)`));
  return m && UUID.test(m[1]) ? m[1] : null;
}

/**
 * Whether this browser was ever used to sign in or check out (it then holds a device id). The landing asks the API who
 * is signed in only then, so a first visit makes no request that answers 401 (ADR-0048). Reads only, writes nothing.
 */
export function knownDevice(): boolean {
  if (typeof document === "undefined") return false; // the build's prerender (ADR-0061)
  try {
    if (localStorage.getItem(KEY)) return true;
  } catch {
    // storage blocked: the cookie decides
  }
  return cookieId() !== null;
}

/** This browser's id: the stored one (storage or cookie), else a new one written to both. */
function deviceId(): string {
  let id: string | null = null;
  try {
    const v = localStorage.getItem(KEY);
    if (v && UUID.test(v)) id = v;
  } catch {
    // storage blocked
  }
  id ??= cookieId() ?? newId();
  try {
    localStorage.setItem(KEY, id);
  } catch {
    // storage blocked: the cookie still holds it
  }
  document.cookie = `${COOKIE}=${id}; Max-Age=${400 * 86_400}; Path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  return id;
}

function canvasTrait(): string {
  try {
    const c = document.createElement("canvas");
    c.width = 240;
    c.height = 60;
    const g = c.getContext("2d");
    if (!g) return "";
    g.textBaseline = "top";
    g.font = "16px Arial";
    g.fillStyle = "#f60";
    g.fillRect(100, 1, 62, 20);
    g.fillStyle = "#069";
    g.fillText("Prop DeskOS <fp> 1.5 Cwm fjord", 2, 15);
    g.fillStyle = "rgba(102, 204, 0, 0.7)";
    g.beginPath();
    g.arc(50, 30, 20, 0, Math.PI * 2);
    g.fill();
    return c.toDataURL();
  } catch {
    return "";
  }
}

function webglTrait(): string {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    return `${vendor}|${renderer}|${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`;
  } catch {
    return "";
  }
}

async function sha256(text: string): Promise<string | null> {
  if (!crypto.subtle) return null;
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(d)].map((x) => x.toString(16).padStart(2, "0")).join("");
}

async function fingerprint(): Promise<string | null> {
  const n = navigator as Navigator & { userAgentData?: { platform?: string }; deviceMemory?: number };
  const traits = [
    n.userAgentData?.platform ?? n.platform ?? "",
    `${screen.width}x${screen.height}x${screen.colorDepth}@${window.devicePixelRatio}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    (n.languages ?? [n.language]).join(","),
    String(n.hardwareConcurrency ?? ""),
    String(n.deviceMemory ?? ""),
    String(n.maxTouchPoints ?? ""),
    await sha256(canvasTrait()),
    webglTrait(),
  ];
  return sha256(traits.join("\n"));
}

let cached: Promise<Record<string, string>> | null = null;

/** The headers to send (computed once per page load). */
export function deviceHeaders(): Promise<Record<string, string>> {
  cached ??= (async () => {
    const out: Record<string, string> = {};
    try {
      out["X-Device-Id"] = deviceId();
    } catch {
      // no storage, no cookie: nothing to send
    }
    try {
      const fp = await fingerprint();
      if (fp) out["X-Device-Fp"] = fp;
    } catch {
      // traits unavailable
    }
    return out;
  })();
  return cached;
}
